import {
  DataProvider,
  fetchUtils,
  RaRecord,
  UpdateManyParams,
  UpdateManyResult,
} from 'react-admin';

import { httpClient } from '../httpClient';
import { GetEventResult } from '../types';

const transformData = (event: any) => {
  return {
    ...event,
    isReported: Number(event.totalReports) > 0,
  };
};

const sanitizeEventUpdateDataFields = (data: Record<string, any>) => {
  const {
    dateCreated,
    isReported,
    moderatorComment,
    parentId,
    pictureLink,
    status,
    totalReports,
    id,
    userCreated,
    userCreatedId,
    categories,
    ...rest
  } = data;

  return {
    ...rest,
    // add additional fields, otherwise API validation fails
    capacity: data.capacity || 0,
    cost: data.cost || 0,
  };
};

const sanitizeEventCreateDataFields = (data: Record<string, any>) => {
  const { dateCreated, ...rest } = data;
  return rest;
};

const isRepeatedEventData = (data: Record<string, any>) =>
  Boolean(data.repeatUntil && data.frequency);

const prepareRepeatedEventData = (data: Record<string, any>) => {
  const { isRepeated, startTime, startDate, endTime, endDate, ...rest } = data;
  const start = new Date(startDate);
  const end = new Date(startDate);
  start.setHours(startTime.getHours());
  start.setMinutes(startTime.getMinutes());
  end.setHours(endTime.getHours());
  end.setMinutes(endTime.getMinutes());
  return {
    startDate: start,
    endDate: end,
    ...rest,
  };
};

export const eventsDataProvider: DataProvider = {
  getManyReference: async (_, params) => {
    const { pagination, sort, id, target } = params;
    const body = JSON.stringify({ pagination, filter: { [target]: id }, sort });

    const eventsList = await httpClient('/admin/event/search', {
      method: 'POST',
      body,
    });

    return {
      data: eventsList.json.data.map((event: GetEventResult) =>
        transformData(event),
      ),
      total: eventsList.json.meta.total,
    };
  },

  getList: async (_, params) => {
    const body = JSON.stringify({ ...params });
    const eventsList = await httpClient('/admin/event/search', {
      method: 'POST',
      body,
    });

    return {
      data: eventsList.json.data.map((event: any) => transformData(event)),
      total: eventsList.json.meta.total,
    };
  },

  getMany: async () => {
    const body = JSON.stringify({});
    const eventsList = await httpClient('/admin/event/search', {
      method: 'POST',
      body,
    });

    return {
      data: eventsList.json.data.map((event: any) => transformData(event)),
      total: eventsList.json.meta.total,
    };
  },

  getOne: async (resource, params) => {
    const body = JSON.stringify({ filter: { eventId: params.id } });
    const eventsList = await httpClient('/admin/event/search', {
      method: 'POST',
      body,
    });

    const eventData = eventsList.json.data[0];
    return {
      data: transformData(eventData),
    };
  },

  update: async (resource, params) => {
    // status only update flow
    if (params.data.method) {
      const body = JSON.stringify(
        params.data.method === 'approve'
          ? {}
          : {
              moderatorComment: params.data.moderatorComment,
            },
      );

      await httpClient(`/admin/event/${params.id}/${params.data.method}`, {
        method: 'PATCH',
        body,
        ...params,
      });

      return {
        data: { id: params.id } as any,
      };
    }

    const { json } = await httpClient(`/events/${params.id}`, {
      method: 'PATCH',
      body: JSON.stringify(sanitizeEventUpdateDataFields(params.data)),
    });

    return {
      data: json,
    };
  },

  deleteMany: async (resource, params) => {
    return await Promise.all(
      params.ids.map((value) =>
        httpClient(`/events/${value}`, {
          method: 'DELETE',
          ...params,
        }),
      ),
    ).then(() => ({ data: [] }));
  },

  delete: async (resource, params) => {
    return await httpClient(`/events/${params.id}`, { method: 'DELETE' }).then(
      ({ json }) => ({ data: json }),
    );
  },

  create: async (resource, params) => {
    const { picture, ...createEventData } = params.data;

    const isRepeatedEvent = isRepeatedEventData(createEventData);

    const route = isRepeatedEvent ? `${resource}/repeated` : resource;
    const eventData = isRepeatedEvent
      ? prepareRepeatedEventData(createEventData)
      : createEventData;

    const response = await httpClient(route, {
      method: 'POST',
      body: JSON.stringify(sanitizeEventCreateDataFields(eventData)),
    });

    const json = isRepeatedEvent ? response.json[0] : response.json;
    const createdEventId = isRepeatedEvent ? json.parentId : json.id;

    if (picture) {
      try {
        const pictureUploadLink = await httpClient(
          `/events/${createdEventId}/create-picture-upload-link`,
          {
            method: 'POST',
            body: JSON.stringify({}),
          },
        );

        await fetchUtils.fetchJson(pictureUploadLink.json.signedLink, {
          method: 'PUT',
          body: picture.rawFile,
          headers: new Headers({
            'Content-Type': picture.rawFile.type,
          }),
        });
      } catch (error) {
        console.error('Error uploading picture:', error);
      }
    }

    return {
      data: json,
    };
  },

  updateMany: function <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateManyParams<any>,
  ): Promise<UpdateManyResult<RecordType>> {
    throw new Error('Function not implemented.');
  },
};
